import { ArrowUpRightIcon } from "lucide-react";
import { useRef } from "react";
import type { AutoplayType } from "embla-carousel-autoplay";
import Autoplay from "embla-carousel-autoplay";
import type { Products as ProductType } from "@repo/fetch-repository/products";
import { Link } from "@repo/i18n/routing";
import {
  Carousel,
  CarouselNext,
  CarouselPrevious,
  CarouselContent,
  CarouselItem,
} from "../ui/carousel";
import VideoPlayer from "./video-player";

export function ProductCarousel(props: {
  items: ProductType[];
  title: string;
  showTextButton: string;
}): React.JSX.Element {
  const plugin = useRef<AutoplayType>(
    Autoplay({
      delay: 5000,
    })
  );
  return (
    <div className="w-full h-full flex flex-col gap-10">
      <h1 className="font-helvetica rtl:font-doran rtl:font-bold text-lg lg:text-3xl font-[100] leading-[1.07] text-[#6a6a6a]">
        {props.title}
      </h1>
      <Carousel
        className="w-full"
        dir="ltr"
        onMouseEnter={plugin.current.stop}
        onMouseLeave={plugin.current.reset}
        plugins={[plugin.current]}
      >
        <CarouselContent>
          {props.items.map((product) =>
            !Array.isArray(product.main_image.data) ? (
              <CarouselItem
                className="lg:basis-1/4 md:basis-1/3 basis-full p-6"
                dir="auto"
                key={product.id}
              >
                <div className="flex flex-col gap-2">
                  <div className="h-[100px] md:h-[280px] lg:h-[200px] mb-4">
                    {product.main_image.data.url.includes("mp4") ? (
                      <div className="size-full object-cover rounded-sm">
                        <VideoPlayer link={product.main_image.data.url} />
                      </div>
                    ) : (
                      <img
                        alt={
                          product.main_image.data.alt
                            ? product.main_image.data.alt
                            : product.name
                        }
                        className="size-full object-cover rounded-sm"
                        onError={(e) => {
                          e.currentTarget.src = "/images/placeholder.png";
                        }}
                        src={
                          product.main_image.data.url ||
                          "/images/placeholder.png"
                        }
                      />
                    )}
                  </div>
                  <h2 className="font-helvetica truncate rtl:font-doran mb-4 rtl:font-medium text-lg lg:text-xl font-light leading-normal text-[#4c4c4c]">
                    {product.name}
                  </h2>
                  <Link
                    className="font-helvetica rtl:font-doran text-sm lg:text-lg font-light leading-[1.41] tracking-normal text-primary flex items-center gap-2"
                    href={`/products/${product.id}/${encodeURIComponent(
                      product.name.replace(/\s+/g, "-").toLowerCase()
                    )}`}
                  >
                    <span>{props.showTextButton}</span>
                    <ArrowUpRightIcon className="size-4" />
                  </Link>
                </div>
              </CarouselItem>
            ) : null
          )}
        </CarouselContent>
        <CarouselPrevious />
        <CarouselNext />
      </Carousel>
    </div>
  );
}

ProductCarousel.displayName = "ProductsCarousel";
